import { useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, silentRequest, apiScope } from '../services/authConfig';

export function useAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const account = accounts[0] || null;

  const signIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await instance.loginPopup(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const getAccessToken = useCallback(async () => {
    if (!account) throw new Error('No account found. Please sign in.');

    try {
      const response = await instance.acquireTokenSilent({ ...silentRequest, account });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Scopes need interactive consent — don't silently popup.
        // Throw a typed error so callers can show a user-initiated re-login prompt.
        const consentError = new Error('NEEDS_CONSENT');
        consentError.code = 'NEEDS_CONSENT';
        throw consentError;
      }
      throw err;
    }
  }, [instance, account]);

  // Token scoped to PeopleVUE's own API — used when calling the function backend.
  // Issues a token with aud: api://APP_ID which can be JWKS-verified server-side.
  const getApiToken = useCallback(async () => {
    if (!account) throw new Error('No account found. Please sign in.');
    try {
      const response = await instance.acquireTokenSilent({
        scopes: [apiScope],
        account,
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: [apiScope],
          account,
        });
        return response.accessToken;
      }
      throw err;
    }
  }, [instance, account]);

  // User-triggered consent flow — call this from a button click
  const requestConsent = useCallback(async () => {
    if (!account) return;
    const response = await instance.acquireTokenPopup({ ...silentRequest, account });
    return response.accessToken;
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    error,
    account,
    signIn,
    signOut,
    getAccessToken,
    getApiToken,
    requestConsent,
  };
}
